import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from '..';
import { raasApi } from '../../api/raas.api';
import {
  ICreateExternalClient,
  IExternalClient,
  IExternalClientCredentials,
  IPagination,
  IUpdateExternalClient,
} from '../../types/raas.types';

interface IRaasState {
  loading: boolean;
  externalClients: IExternalClient[];
  selectedClient: IExternalClient | null;
  credentials: IExternalClientCredentials | null;
  pagination: IPagination | null;
}

const initialState: IRaasState = {
  loading: false,
  externalClients: [],
  selectedClient: null,
  credentials: null,
  pagination: null,
};

export const raasSlice = createSlice({
  name: 'raas',
  initialState,
  reducers: {
    clearCredentials: (state) => {
      state.credentials = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createExternalClient.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createExternalClient.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.data) {
        state.credentials = {
          apiKey: payload.data.apiKey || '',
          webhookSecret: payload.data.webhookSecret || '',
          provider: payload.data.provider,
        };
        if (payload.data.provider) {
          state.externalClients.push(payload.data.provider);
        }
      }
      toast.success(payload?.message || 'External client created successfully');
    });
    builder.addCase(createExternalClient.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to create external client');
    });

    builder.addCase(getAllExternalClients.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllExternalClients.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.externalClients = payload?.data?.providers || [];
      state.pagination = payload?.data?.pagination || null;
    });
    builder.addCase(getAllExternalClients.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to fetch external clients');
    });

    builder.addCase(getExternalClient.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getExternalClient.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.selectedClient = payload?.data || null;
    });
    builder.addCase(getExternalClient.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to fetch external client');
    });

    builder.addCase(activateClient.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(activateClient.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.data?.id) {
        const client = state.externalClients.find((c) => c.id === payload.data.id);
        if (client) {
          client.active = payload.data.active;
        }
      }
      toast.success('Client activated successfully');
    });
    builder.addCase(activateClient.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to activate client');
    });

    builder.addCase(deactivateClient.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deactivateClient.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.data?.id) {
        const client = state.externalClients.find((c) => c.id === payload.data.id);
        if (client) {
          client.active = payload.data.active;
        }
      }
      toast.success('Client deactivated successfully');
    });
    builder.addCase(deactivateClient.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to deactivate client');
    });

    builder.addCase(updateExternalClient.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateExternalClient.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.data?.id) {
        const index = state.externalClients.findIndex(
          (c) => c.id === payload.data.id
        );
        if (index !== -1) {
          state.externalClients[index] = payload.data;
        }
        state.selectedClient = payload.data;
      }
      toast.success(payload?.message || 'Client updated successfully');
    });
    builder.addCase(updateExternalClient.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to update client');
    });

    builder.addCase(rotateApiKey.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rotateApiKey.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.data?.provider?.id) {
        const index = state.externalClients.findIndex(
          (c) => c.id === payload.data.provider.id
        );
        if (index !== -1) {
          state.externalClients[index] = payload.data.provider;
        }
        state.selectedClient = payload.data.provider;
        state.credentials = {
          apiKey: payload.data.newApiKey,
          webhookSecret: state.credentials?.webhookSecret || '',
          provider: payload.data.provider,
        };
      }
      toast.success('API key rotated successfully');
    });
    builder.addCase(rotateApiKey.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to rotate API key');
    });

    builder.addCase(rotateWebhookSecret.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(rotateWebhookSecret.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload?.data?.provider?.id) {
        const index = state.externalClients.findIndex(
          (c) => c.id === payload.data.provider.id
        );
        if (index !== -1) {
          state.externalClients[index] = payload.data.provider;
        }
        state.selectedClient = payload.data.provider;
        state.credentials = {
          apiKey: state.credentials?.apiKey || '',
          webhookSecret: payload.data.newWebhookSecret,
          provider: payload.data.provider,
        };
      }
      toast.success('Webhook secret rotated successfully');
    });
    builder.addCase(rotateWebhookSecret.rejected, (state) => {
      state.loading = false;
      toast.error('Failed to rotate webhook secret');
    });
  },
});

export const createExternalClient = createAsyncThunk(
  'raas/createExternalClient',
  async (params: { payload: ICreateExternalClient; cb?: () => void }) => {
    try {
      const { payload, cb } = params;
      const data = await raasApi.createExternalClient(payload);
      cb?.();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const getAllExternalClients = createAsyncThunk(
  'raas/getAllExternalClients',
  async (params?: { page?: number; perPage?: number; active?: boolean }) => {
    try {
      const data = await raasApi.getAllExternalClients(params);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const getExternalClient = createAsyncThunk(
  'raas/getExternalClient',
  async (id: number) => {
    try {
      const data = await raasApi.getExternalClient(id);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const activateClient = createAsyncThunk(
  'raas/activateClient',
  async (params: { id: number; cb?: () => void }) => {
    try {
      const { id, cb } = params;
      const data = await raasApi.activateClient(id);
      cb?.();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const deactivateClient = createAsyncThunk(
  'raas/deactivateClient',
  async (params: { id: number; cb?: () => void }) => {
    try {
      const { id, cb } = params;
      const data = await raasApi.deactivateClient(id);
      cb?.();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const updateExternalClient = createAsyncThunk(
  'raas/updateExternalClient',
  async (params: {
    id: number;
    payload: IUpdateExternalClient;
    cb?: () => void;
  }) => {
    try {
      const { id, payload, cb } = params;
      const data = await raasApi.updateExternalClient(id, payload);
      cb?.();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const rotateApiKey = createAsyncThunk(
  'raas/rotateApiKey',
  async (params: { id: number; cb?: () => void }) => {
    try {
      const { id, cb } = params;
      const data = await raasApi.rotateApiKey(id);
      cb?.();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const rotateWebhookSecret = createAsyncThunk(
  'raas/rotateWebhookSecret',
  async (params: { id: number; cb?: () => void }) => {
    try {
      const { id, cb } = params;
      const data = await raasApi.rotateWebhookSecret(id);
      cb?.();
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const raasSelector = (state: RootState) => state.raas;
export const { clearCredentials } = raasSlice.actions;
export default raasSlice.reducer;
